import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
} from '@angular/core';

export interface LbaActionMenuItem {
  id: string;
  text: string;
  icon?: string;
  disabled?: boolean;
}

@Component({
  selector: 'lba-action-menu',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './lba-action-menu.component.html',
  styleUrls: ['./lba-action-menu.component.scss'],
})
export class LbaActionMenuComponent {
  @Input() items: LbaActionMenuItem[] = [];
  @Input() panelWidth: string | number = 280;
  @Input() disabled = false;

  @Output() actionSelect = new EventEmitter<LbaActionMenuItem>();
  @Output() openedChange = new EventEmitter<boolean>();

  isOpen = false;

  /** Ignores the same click that opened the menu when listening on document. */
  private ignoreDocumentClick = false;

  @HostBinding('class.is-open')
  get openClass(): boolean {
    return this.isOpen;
  }

  constructor(private host: ElementRef<HTMLElement>) { }

  get panelMinWidth(): number {
    return typeof this.panelWidth === 'number'
      ? this.panelWidth
      : parseInt(String(this.panelWidth), 10) || 280;
  }

  trackByItemId(_: number, item: LbaActionMenuItem): string {
    return item.id;
  }

  toggle(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.close();
      return;
    }
    this.open();
  }

  open(): void {
    if (this.disabled || this.isOpen) {
      return;
    }
    this.isOpen = true;
    this.ignoreDocumentClick = true;
    this.openedChange.emit(true);
    // Allow the opening click to finish before outside-click closes the menu.
    setTimeout(() => {
      this.ignoreDocumentClick = false;
    });
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.ignoreDocumentClick = false;
    this.openedChange.emit(false);
  }

  onItemClick(item: LbaActionMenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (item.disabled) {
      return;
    }
    this.actionSelect.emit(item);
    this.close();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen || this.ignoreDocumentClick) {
      return;
    }
    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) {
      return;
    }
    this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }
}
